import { Injectable, signal } from '@angular/core';
import { BaseFilterService } from '../../../shared/services/base-filter.service';

export interface ReconStatementFilters {
  frequency: string;
  division: string[];
  entity: string[];
  vendorGroup: string[];
  classification: string[];
  period: string[];
}

export type ReconStatementArrayFilter = Exclude<keyof ReconStatementFilters, 'frequency'>;

export type ReconStatementFilterSyncResult =
  | { synchronized: true; incompatibleFields: [] }
  | { synchronized: false; incompatibleFields: ReconStatementArrayFilter[] };

const ARRAY_FILTER_FIELDS: ReconStatementArrayFilter[] = [
  'division',
  'entity',
  'vendorGroup',
  'classification',
  'period',
];

const EMPTY_FILTERS: ReconStatementFilters = {
  frequency: 'Quarterly',
  division: [],
  entity: [],
  vendorGroup: [],
  classification: [],
  period: [],
};

@Injectable({ providedIn: 'root' })
export class ReconStatementFilterService extends BaseFilterService<ReconStatementFilters> {
  protected readonly storageKey = 'recon_statement_filters';
  protected readonly emptyFilters = EMPTY_FILTERS;

  /** True after the first explicit applyFilters() call */
  ready = signal(false);

  /** True while the grid data is loading */
  gridLoading = signal(false);

  constructor() {
    super();
    this.init();
  }

  override applyFilters(filters: ReconStatementFilters): void {
    super.applyFilters(filters);
    this.ready.set(true);
  }

  synchronizeFromSharedFilters(filters: ReconStatementFilters): ReconStatementFilterSyncResult {
    const incompatibleFields = ARRAY_FILTER_FIELDS.filter(field => filters[field].length > 1);
    if (incompatibleFields.length > 0) {
      return { synchronized: false, incompatibleFields };
    }

    this.applyFilters({
      frequency: filters.frequency,
      division: [...filters.division],
      entity: [...filters.entity],
      vendorGroup: [...filters.vendorGroup],
      classification: [...filters.classification],
      period: [...filters.period],
    });

    return { synchronized: true, incompatibleFields: [] };
  }

  /** Full reset — call on logout so the singleton is clean for the next session */
  reset(): void {
    this.resetFilters();
    this.ready.set(false);
    this.gridLoading.set(false);
  }

  get hasFilters(): boolean {
    const f = this.filters();
    return !!(f.entity?.length || f.vendorGroup?.length || f.period?.length);
  }

  buildPayload(filters?: ReconStatementFilters, user?: { userId: number; roleId: number } | null): Record<string, any> {
    const f = filters ?? this.filters();
    return {
      entityName: f.entity?.length ? f.entity.join(',') : null,
      division: f.division?.length ? f.division.join(',') : null,
      vendorName: f.vendorGroup?.length ? f.vendorGroup.join(',') : null,
      classification: f.classification?.length ? f.classification.join(',') : null,
      period: f.period?.length ? f.period[0] : null,
      frequency: f.frequency || null,
      userId: user?.userId?.toString() ?? null,
      roleId: user?.roleId?.toString() ?? null,
    };
  }

  protected parseStored(parsed: any): ReconStatementFilters {
    return {
      frequency: typeof parsed.frequency === 'string' ? parsed.frequency : 'Quarterly',
      division: Array.isArray(parsed.division) ? parsed.division : [],
      entity: Array.isArray(parsed.entity) ? parsed.entity : [],
      vendorGroup: Array.isArray(parsed.vendorGroup) ? parsed.vendorGroup : [],
      classification: Array.isArray(parsed.classification) ? parsed.classification : [],
      period: Array.isArray(parsed.period) ? parsed.period : [],
    };
  }
}
